// Conversación de WhatsApp: encabezado, burbujas, sugerencia del agente y compositor.
//
// Reusa lo que ya existe en Orión: el mismo envío del takeover
// (POST /threads/{id}/human-message, ahora con `from_suggestion`), el mismo
// control de pausa (<AgentControl>) y la misma pausa por conversación. Lo nuevo
// es la sugerencia como tarjeta (enviar / editar / descartar), el modo por
// conversación y la ventana de 24 h con plantillas aprobadas.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle, ArrowLeft, Bot, Check, Clock, ExternalLink, FileText, Hand, Loader2, Maximize2, Mic,
  Minimize2, Pencil, Send, Sparkles, User, X, Zap,
} from 'lucide-react';

import { AgentControl } from '@/components/AgentControl';
import { pauseThreadAgent } from '@/lib/api';
import {
  dismissSuggestion, getMessages, getTemplates, markRead, MODE_LABEL, renderTemplate, sendTemplate,
  sendText, setThreadMode, WaApiError,
  type ReplyMode, type WaChat, type WaMessage, type WaTemplate, type WaThreadState,
} from '../api';
import { Avatar, dayLabel, prettyPhone, relativeUntil, sameDay, STAGE_LABEL, timeOnly } from './shared';

type Notify = (type: 'success' | 'error' | 'info', title: string, message?: string) => void;

const POLL_MS = 6000;

export function chatName(c: Pick<WaChat, 'person' | 'telefono'>): string {
  return c.person?.name?.trim() || prettyPhone(c.telefono) || 'Cliente';
}

export function Conversation({
  chat, onBack, onChanged, leadHref, notify, isMobile, expanded = false, onToggleExpand,
}: {
  chat: WaChat;
  onBack: () => void;
  /** Móvil: la conversación ocupa toda la pantalla. */
  expanded?: boolean;
  onToggleExpand?: () => void;
  onChanged: () => void;
  leadHref: string | null;
  notify: Notify;
  isMobile: boolean;
}) {
  const [msgs, setMsgs] = useState<WaMessage[]>([]);
  const [wa, setWa] = useState<WaThreadState | null>(null);
  const [status, setStatus] = useState<'loading' | 'ok' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [paused, setPaused] = useState({
    paused: !!chat.agent_paused, until: chat.agent_paused_until ?? null, indefinite: !!chat.agent_paused_indefinite,
  });
  const navigate = useNavigate();
  const go = (e: { preventDefault: () => void }, href: string) => { e.preventDefault(); navigate(href); };
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const stick = useRef(true);
  const inflight = useRef(false);

  const load = useCallback(async (first = false) => {
    if (inflight.current) return;
    inflight.current = true;
    try {
      const r = await getMessages(chat.id);
      setMsgs(r.messages || []);
      setWa(r.thread?.whatsapp ?? null);
      setStatus('ok');
      if (first) markRead(chat.id).then(onChanged).catch(() => {});
    } catch (e: any) {
      if (first) {
        setStatus('error');
        setError(e?.message || 'No se pudo abrir la conversación');
      }
    } finally {
      inflight.current = false;
    }
  }, [chat.id, onChanged]);

  useEffect(() => {
    setStatus('loading');
    setMsgs([]);
    setWa(null);
    stick.current = true;
    setPaused({ paused: !!chat.agent_paused, until: chat.agent_paused_until ?? null, indefinite: !!chat.agent_paused_indefinite });
    load(true);
    const id = window.setInterval(() => { if (document.visibilityState === 'visible') load(); }, POLL_MS);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat.id]);

  // Mantener el scroll abajo solo si la persona ya estaba abajo.
  useEffect(() => {
    const el = scrollRef.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [msgs, wa?.pending_suggestion?.text]);
  const onScroll = () => {
    const el = scrollRef.current;
    if (el) stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const name = chatName(chat);
  const stage = chat.person?.stage ? STAGE_LABEL[chat.person.stage] || chat.person.stage : null;
  const effective: ReplyMode = paused.paused ? 'suggest' : (wa?.configured_mode ?? 'auto');
  const agentWrites = !paused.paused && effective === 'auto';
  const pending = wa?.pending_suggestion ?? null;
  const windowOpen = wa ? wa.window_open : !!chat.window_open;

  // Historial: las sugerencias resueltas como «enviada/editada» ya tienen su turno real.
  const visible = useMemo(
    () => msgs.filter((m) => !(m.suggestion && (m.suggestion_state === 'sent' || m.suggestion_state === 'edited' || !m.suggestion_state))),
    [msgs],
  );

  /* ── Compositor ── */
  const [text, setText] = useState('');
  const [fromSuggestion, setFromSuggestion] = useState(false);
  const [sending, setSending] = useState(false);
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => { setText(''); setFromSuggestion(false); }, [chat.id]);
  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 160)}px`;
  }, [text]);

  const send = async (body: string, viaSuggestion: boolean) => {
    const t = body.trim();
    if (!t || sending) return;
    setSending(true);
    try {
      await sendText(chat.id, t, viaSuggestion);
      setText('');
      setFromSuggestion(false);
      stick.current = true;
      await load();
      onChanged();
    } catch (e: any) {
      notify('error', 'No se envió el mensaje', e instanceof WaApiError ? e.message : e?.message);
    } finally {
      setSending(false);
    }
  };

  const discard = async () => {
    try {
      await dismissSuggestion(chat.id);
      await load();
      onChanged();
    } catch (e: any) {
      notify('error', 'No se descartó la sugerencia', e?.message);
    }
  };

  const editSuggestion = () => {
    if (!pending) return;
    setText(pending.text);
    setFromSuggestion(true);
    requestAnimationFrame(() => taRef.current?.focus());
  };

  const takeOver = async () => {
    try {
      const t = await pauseThreadAgent({ thread_id: chat.id, session_id: chat.session_id, telefono: chat.telefono }, 30);
      setPaused({ paused: !!t.agent_paused, until: t.agent_paused_until ?? null, indefinite: !!t.agent_paused_indefinite });
      onChanged();
      requestAnimationFrame(() => taRef.current?.focus());
    } catch (e: any) {
      notify('error', 'No se pudo tomar el control', e?.message);
    }
  };

  const [modeBusy, setModeBusy] = useState(false);
  const changeThreadMode = async (v: string) => {
    setModeBusy(true);
    try {
      await setThreadMode(chat.id, v === 'inherit' ? null : (v as ReplyMode));
      await load();
      onChanged();
    } catch (e: any) {
      notify('error', 'No se cambió el modo de esta conversación', e?.message);
    } finally {
      setModeBusy(false);
    }
  };

  return (
    <section className="flex h-full min-h-0 flex-col" aria-label={`Conversación con ${name}`}>
      {/* Encabezado */}
      <header className="flex items-center gap-3 border-b border-border bg-card px-3 py-2.5 sm:px-4">
        {isMobile && (
          <button onClick={onBack} className="-ml-1 rounded-lg p-2 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))]" aria-label="Volver a los chats">
            <ArrowLeft className="h-5 w-5" />
          </button>
        )}
        <Avatar name={name} size={40} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            <h2 className="truncate text-[15px] font-semibold">{name}</h2>
            {stage && (
              leadHref ? (
                <a href={leadHref} onClick={(e) => go(e, leadHref)} className="hidden sm:inline-flex shrink-0 items-center gap-1 rounded-md border border-border px-1.5 py-0.5 text-[11px] font-medium hover:bg-muted" title="Abrir la ficha del lead">
                  {stage} <ExternalLink className="h-3 w-3" aria-hidden />
                </a>
              ) : (
                <span className="hidden sm:inline shrink-0 rounded-md border border-border px-1.5 py-0.5 text-[11px] font-medium">{stage}</span>
              )
            )}
          </div>
          <p className="truncate text-[12px] text-muted-foreground tabular-nums">
            {chat.person?.name ? prettyPhone(chat.telefono) : (leadHref ? <a href={leadHref} onClick={(e) => go(e, leadHref)} className="underline-offset-2 hover:underline">Buscar en leads</a> : 'Sin ficha en el CRM')}
          </p>
        </div>
        <div className="hidden xl:flex items-center gap-2">
          <label className="sr-only" htmlFor={`mode-${chat.id}`}>Modo de esta conversación</label>
          <select
            id={`mode-${chat.id}`}
            value={wa?.reply_mode ?? 'inherit'}
            disabled={modeBusy || !wa}
            onChange={(e) => changeThreadMode(e.target.value)}
            className="max-w-[13rem] rounded-lg border border-border bg-card px-2 py-1.5 text-[12px] text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))]"
            title="Solo esta conversación. La pausa siempre gana."
          >
            <option value="inherit">Igual que el número{!wa?.reply_mode && wa ? ` (${MODE_LABEL[wa.configured_mode]})` : ''}</option>
            <option value="auto">Automático aquí</option>
            <option value="suggest">Solo sugiere aquí</option>
            <option value="off">Apagado aquí</option>
          </select>
        </div>
        <AgentControl
          conv={{ thread_id: chat.id, session_id: chat.session_id, telefono: chat.telefono }}
          paused={paused.paused}
          pausedUntil={paused.until}
          indefinite={paused.indefinite}
          onChange={(t) => { setPaused({ paused: !!t.agent_paused, until: t.agent_paused_until ?? null, indefinite: !!t.agent_paused_indefinite }); load(); onChanged(); }}
          className="shrink-0 [&>span]:hidden"
        />
        {isMobile && onToggleExpand && (
          <button type="button" onClick={onToggleExpand} aria-pressed={expanded}
            className="shrink-0 rounded-lg p-2 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))]"
            aria-label={expanded ? 'Reducir la conversación' : 'Ampliar la conversación a pantalla completa'}
            title={expanded ? 'Reducir' : 'Pantalla completa'}>
            {expanded ? <Minimize2 className="h-5 w-5" aria-hidden /> : <Maximize2 className="h-5 w-5" aria-hidden />}
          </button>
        )}
      </header>

      {/* Mensajes */}
      <div ref={scrollRef} onScroll={onScroll} className="wa-wallpaper wa-scroll relative flex-1 min-h-0 overflow-y-auto px-3 py-4 sm:px-6" aria-live="polite" aria-busy={status === 'loading'}>
        {status === 'loading' && (
          <div className="space-y-3" aria-hidden>
            {[60, 40, 72, 50].map((w, i) => (
              <div key={i} className={`h-12 rounded-2xl bg-card/70 animate-pulse ${i % 2 ? 'ml-auto' : ''}`} style={{ width: `${w}%` }} />
            ))}
          </div>
        )}
        {status === 'error' && (
          <div role="alert" className="mx-auto max-w-md rounded-xl border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
        )}
        {status === 'ok' && visible.length === 0 && (
          <p className="mx-auto mt-10 max-w-sm rounded-xl bg-card/80 px-4 py-3 text-center text-[13px] text-muted-foreground">Aún no hay mensajes guardados en esta conversación.</p>
        )}
        <ol className="mx-auto flex max-w-3xl flex-col gap-1.5">
          {visible.map((m, i) => {
            const prev = visible[i - 1];
            return (
              <li key={m.id} className="flex flex-col">
                {(!prev || !sameDay(prev.at, m.at)) && (
                  <div className="my-2 self-center rounded-lg bg-card/90 px-2.5 py-1 text-[12px] font-medium text-muted-foreground shadow-sm first-letter:uppercase">{dayLabel(m.at)}</div>
                )}
                <Bubble m={m} />
              </li>
            );
          })}
        </ol>
      </div>

      {/* Sugerencia del agente */}
      {pending && (
        <div className="border-t border-border bg-card px-3 pt-3 sm:px-4">
          <div className="wa-suggest wa-in mx-auto max-w-3xl rounded-2xl border p-3">
            <div className="flex items-center gap-2 text-[12px] font-semibold wa-suggest-fg">
              <Sparkles className="h-4 w-4" aria-hidden />
              Sugerencia del agente
              <span className="font-normal opacity-80">· {pending.source === 'pause' ? 'tienes el control de esta conversación' : 'no se ha enviado'}</span>
            </div>
            <p className="mt-1.5 whitespace-pre-wrap break-words text-[14px] leading-relaxed text-foreground">{pending.text}</p>
            <div className="mt-2.5 flex flex-wrap items-center gap-2">
              <button type="button" disabled={sending || !windowOpen} onClick={() => send(pending.text, true)}
                className="wa-btn inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[13px] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[hsl(var(--wa-accent))]"
                title={windowOpen ? 'Enviar tal cual' : 'Pasaron 24 h: solo se pueden mandar plantillas'}>
                {sending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Send className="h-4 w-4" aria-hidden />} Enviar
              </button>
              <button type="button" onClick={editSuggestion} disabled={!windowOpen}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-[13px] font-medium hover:bg-muted disabled:opacity-50">
                <Pencil className="h-4 w-4" aria-hidden /> Editar
              </button>
              <button type="button" onClick={discard}
                className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[13px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground">
                <X className="h-4 w-4" aria-hidden /> Descartar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Compositor */}
      <footer className="border-t border-border bg-card px-3 py-3 sm:px-4 pb-[calc(12px+env(safe-area-inset-bottom))]">
        <div className="mx-auto max-w-3xl">
          {!windowOpen ? (
            <TemplateComposer chatId={chat.id} lastCustomerAt={wa?.last_customer_at ?? chat.last_customer_at ?? null}
              onSent={async () => { await load(); onChanged(); }} notify={notify} />
          ) : agentWrites ? (
            <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-muted/60 px-3 py-2.5">
              <Bot className="h-5 w-5 shrink-0 wa-accent-text" aria-hidden />
              <p className="flex-1 min-w-[12rem] text-[13px] text-muted-foreground">
                El agente está respondiendo esta conversación. Para escribir tú, toma el control: el agente te dejará sus respuestas como sugerencia.
              </p>
              <button type="button" onClick={takeOver}
                className="wa-btn inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[13px] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[hsl(var(--wa-accent))]">
                <Hand className="h-4 w-4" aria-hidden /> Tomar control y escribir
              </button>
            </div>
          ) : (
            <form
              onSubmit={(e) => { e.preventDefault(); send(text, fromSuggestion); }}
              className="flex items-end gap-2"
            >
              <div className="flex-1 rounded-2xl border border-border bg-background px-3 py-2 focus-within:ring-2 focus-within:ring-[hsl(var(--wa-accent))]">
                {fromSuggestion && (
                  <div className="mb-1 flex items-center gap-1.5 text-[11px] font-medium wa-suggest-fg">
                    <Sparkles className="h-3 w-3" aria-hidden /> Editando la sugerencia del agente
                    <button type="button" onClick={() => { setFromSuggestion(false); setText(''); }} className="ml-auto text-muted-foreground hover:text-foreground" aria-label="Cancelar edición">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
                <label htmlFor={`msg-${chat.id}`} className="sr-only">Escribe un mensaje</label>
                <textarea
                  id={`msg-${chat.id}`}
                  ref={taRef}
                  rows={1}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(text, fromSuggestion); }
                  }}
                  placeholder="Escribe un mensaje"
                  className="block w-full resize-none bg-transparent text-[15px] leading-6 outline-none placeholder:text-muted-foreground"
                />
              </div>
              <button type="submit" disabled={!text.trim() || sending} aria-label="Enviar mensaje"
                className="wa-btn grid h-11 w-11 shrink-0 place-items-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[hsl(var(--wa-accent))]">
                {sending ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden /> : <Send className="h-5 w-5" aria-hidden />}
              </button>
            </form>
          )}
          {windowOpen && wa?.window_expires_at && (
            <p className="mt-1.5 flex items-center gap-1 text-[11px] text-muted-foreground">
              <Clock className="h-3 w-3" aria-hidden /> Ventana de WhatsApp abierta · cierra en {relativeUntil(wa.window_expires_at)}
              {effective !== 'auto' && !isMobile && <span className="ml-1">· Enter envía, Shift+Enter salto de línea</span>}
            </p>
          )}
        </div>
      </footer>
    </section>
  );
}

/* ── Burbuja ─────────────────────────────────────────────────────── */

function Bubble({ m }: { m: WaMessage }) {
  const out = m.role === 'assistant';
  const text = m.text ?? (m.bloques || []).filter((b) => b.tipo === 'texto').map((b) => b.contenido).join('\n\n');
  const media = m.media ?? [];
  if (m.suggestion) {
    return (
      <div className="self-end max-w-[85%] sm:max-w-[70%] rounded-2xl border border-dashed border-border bg-card/70 px-3 py-2 text-[13px] text-muted-foreground">
        <div className="mb-0.5 flex items-center gap-1 text-[11px] font-medium"><Sparkles className="h-3 w-3" aria-hidden />
          Borrador del agente · {m.suggestion_state === 'dismissed' ? 'descartado' : 'no se envió'}</div>
        <p className="line-clamp-3 whitespace-pre-wrap break-words">{text}</p>
      </div>
    );
  }
  const who = m.author === 'team' ? { Icon: User, label: m.author_name ? `${m.author_name}` : 'Equipo' }
    : m.author === 'automation' ? { Icon: Zap, label: m.author_name || 'Automático' }
      : out ? { Icon: Bot, label: 'Agente' } : null;
  return (
    <div className={`wa-in max-w-[85%] sm:max-w-[70%] rounded-2xl px-3 pb-1.5 pt-2 shadow-sm ${out ? 'wa-bubble-out self-end rounded-br-md' : 'self-start rounded-bl-md bg-card text-card-foreground'}`}>
      {media.map((x, i) => <MediaView key={i} tipo={x.tipo} url={x.url} />)}
      {text && <p className="whitespace-pre-wrap break-words text-[14.5px] leading-relaxed">{text}</p>}
      <div className={`mt-0.5 flex items-center justify-end gap-1.5 text-[11px] ${out ? 'opacity-75' : 'text-muted-foreground'}`}>
        {who && <span className="inline-flex items-center gap-1"><who.Icon className="h-3 w-3" aria-hidden />{who.label}</span>}
        {who && <span aria-hidden>·</span>}
        <time dateTime={m.at}>{timeOnly(m.at)}</time>
        {out && <Check className="h-3 w-3" aria-label="enviado" />}
      </div>
    </div>
  );
}

function MediaView({ tipo, url }: { tipo: string; url: string }) {
  if (tipo === 'imagen') {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className="mb-1.5 block overflow-hidden rounded-xl">
        <img src={url} alt="Imagen enviada por el cliente" loading="lazy" className="max-h-72 w-full object-cover" />
      </a>
    );
  }
  if (tipo === 'audio') {
    return (
      <div className="mb-1.5 flex items-center gap-2">
        <Mic className="h-4 w-4 shrink-0" aria-hidden />
        <audio controls preload="none" src={url} className="h-9 max-w-full" aria-label="Nota de voz" />
      </div>
    );
  }
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="mb-1.5 flex items-center gap-2 rounded-xl border border-border bg-background/60 px-2.5 py-2 text-[13px] hover:bg-background">
      <FileText className="h-4 w-4 shrink-0" aria-hidden /> {tipo === 'video' ? 'Video' : 'Documento'} <ExternalLink className="ml-auto h-3.5 w-3.5" aria-hidden />
    </a>
  );
}

/* ── Plantillas (ventana de 24 h vencida) ───────────────────────── */

function TemplateComposer({ chatId, lastCustomerAt, onSent, notify }: {
  chatId: string;
  lastCustomerAt: string | null;
  onSent: () => Promise<void> | void;
  notify: Notify;
}) {
  const [tpls, setTpls] = useState<WaTemplate[] | null>(null);
  const [motivo, setMotivo] = useState<string | null>(null);
  const [sel, setSel] = useState<string>('');
  const [vars, setVars] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  // Plegado por defecto: la conversación debe seguir visible (Samuel, 23-oct).
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    getTemplates()
      .then((r) => {
        if (!alive) return;
        const ok = (r.templates || []).filter((t) => t.sendable);
        setTpls(ok);
        setMotivo(r.motivo || null);
        if (ok[0]) setSel(ok[0].name);
      })
      .catch((e) => { if (alive) { setTpls([]); setMotivo(e?.message || 'No se pudieron cargar las plantillas'); } });
    return () => { alive = false; };
  }, []);

  const tpl = tpls?.find((t) => t.name === sel) || null;
  useEffect(() => { setVars(Array.from({ length: tpl?.variables || 0 }, () => '')); }, [tpl?.name, tpl?.variables]);
  const ready = !!tpl && vars.every((v) => v.trim());

  const send = async () => {
    if (!tpl || !ready) return;
    setSending(true);
    try {
      await sendTemplate(chatId, tpl.name, tpl.language, vars.map((v) => v.trim()));
      notify('success', 'Plantilla enviada');
      setVars(vars.map(() => ''));
      await onSent();
    } catch (e: any) {
      notify('error', 'No se envió la plantilla', e?.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-3">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 shrink-0 text-amber-700 dark:text-amber-300" aria-hidden />
        <p className="flex-1 min-w-0 text-[13px] text-foreground" title={`Pasaron más de 24 horas desde el último mensaje de este cliente${lastCustomerAt ? ` (${dayLabel(lastCustomerAt).toLowerCase()} ${timeOnly(lastCustomerAt)})` : ''}. WhatsApp solo permite enviar una plantilla aprobada hasta que vuelva a escribir.`}>
          <strong>Pasaron 24 h</strong>
          <span className="text-muted-foreground"> · solo se puede enviar una plantilla aprobada</span>
        </p>
        <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
          className="shrink-0 rounded-lg border border-amber-500/50 bg-card px-3 py-1.5 text-[13px] font-medium text-foreground hover:bg-muted">
          {open ? 'Cerrar' : 'Elegir plantilla'}
        </button>
      </div>
      {open && (<div className="mt-1 max-h-[45vh] overflow-y-auto overscroll-contain pr-1">
      {tpls === null ? (
        <div className="mt-3 h-10 animate-pulse rounded-lg bg-card/70" aria-hidden />
      ) : tpls.length === 0 ? (
        <p className="mt-2 text-[13px] text-muted-foreground">{motivo || 'No hay plantillas aprobadas que se puedan enviar desde aquí.'} Revisa Configuración → Plantillas.</p>
      ) : (
        <div className="mt-3 grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
          <div className="space-y-2">
            <label className="block text-[12px] font-medium text-muted-foreground" htmlFor={`tpl-${chatId}`}>Plantilla</label>
            <select id={`tpl-${chatId}`} value={sel} onChange={(e) => setSel(e.target.value)}
              className="w-full rounded-lg border border-border bg-card px-2.5 py-2 text-[14px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))]">
              {tpls.map((t) => <option key={t.name} value={t.name}>{t.name} · {t.language}</option>)}
            </select>
            {vars.map((v, i) => (
              <div key={i}>
                <label className="block text-[12px] font-medium text-muted-foreground" htmlFor={`tpl-${chatId}-v${i}`}>Variable {`{{${i + 1}}}`}</label>
                <input id={`tpl-${chatId}-v${i}`} value={v} onChange={(e) => setVars(vars.map((x, j) => (j === i ? e.target.value : x)))}
                  className="w-full rounded-lg border border-border bg-card px-2.5 py-2 text-[14px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))]" />
              </div>
            ))}
          </div>
          <div className="flex flex-col">
            <span className="mb-1 text-[12px] font-medium text-muted-foreground">Vista previa</span>
            <div className="wa-wallpaper flex-1 rounded-xl p-3">
              <div className="wa-bubble-out ml-auto max-w-[95%] rounded-2xl rounded-br-md px-3 py-2 text-[14px] shadow-sm whitespace-pre-wrap break-words">
                {tpl?.header && <strong className="mb-1 block">{tpl.header}</strong>}
                {tpl ? renderTemplate(tpl.body, vars) : ''}
              </div>
            </div>
            <button type="button" onClick={send} disabled={!ready || sending}
              className="wa-btn mt-2 inline-flex items-center justify-center gap-1.5 self-end rounded-lg px-3 py-2 text-[13px] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[hsl(var(--wa-accent))]">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Send className="h-4 w-4" aria-hidden />} Enviar plantilla
            </button>
          </div>
        </div>
      )}
      </div>)}
    </div>
  );
}
